import React from 'react';
import { Clock3Icon, PlusIcon, Trash2Icon } from 'lucide-react';
import type { DailyTimeBlock } from '../utils/timeBlocks';
import { WEEKDAYS, WEEKDAY_PRESET, formatTimeBlock, hasWeekdays, isTimeBlockActive, isValidTimeBlock, toggleWeekday } from '../utils/timeBlocks';
interface TimeBlocksCellProps {
  blocks: DailyTimeBlock[];
  now: Date;
  officerName: string;
  onChange: (blocks: DailyTimeBlock[]) => void;
}
function createBlock(): DailyTimeBlock {
  return {
    id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    days: [...WEEKDAY_PRESET],
    start: '09:00',
    end: '17:00'
  };
}
export function TimeBlocksCell({
  blocks,
  now,
  officerName,
  onChange
}: TimeBlocksCellProps) {
  const updateTime = (id: string, field: 'start' | 'end', value: string) => {
    onChange(blocks.map(block => block.id === id ? {
      ...block,
      [field]: value
    } : block));
  };
  const updateDays = (id: string, dayIndex: number) => {
    onChange(blocks.map(block => block.id === id ? {
      ...block,
      days: toggleWeekday(block.days, dayIndex)
    } : block));
  };
  const addBlock = () => onChange([...blocks, createBlock()]);
  const removeBlock = (id: string) => onChange(blocks.filter(block => block.id !== id));
  const hasInvalidTimes = blocks.some(block => !isValidTimeBlock(block));
  const hasMissingDays = blocks.some(block => !hasWeekdays(block));
  return <td className="min-w-[350px] px-5 py-3.5 align-middle" data-id="element-141" data-mp="bmkucwd4xral">
      <div className="space-y-2" data-id="element-142" data-mp="bp71ww0106st">
        {blocks.map((block, index) => {
        const valid = isValidTimeBlock(block);
        const active = isTimeBlockActive(block, now);
        return <div key={block.id} className={`rounded-md border px-2.5 py-2 ${active ? 'border-rcm-200 bg-rcm-50/60' : 'border-slate-200 bg-white'}`} data-id="element-143" data-mp="lj7chde2u48x" data-mpi={index}>
              <div className="flex items-center gap-1.5" data-id="element-144" data-mp="2ncwvu5mlg4da">
                <Clock3Icon className={`h-3.5 w-3.5 flex-none ${active ? 'text-rcm-500' : 'text-slate-300'}`} aria-hidden="true" data-id="element-145" data-mp="2iebxkbqwjurh" />
                <label className="sr-only" htmlFor={`${block.id}-start`} data-id="element-146" data-mp="383ac1o1rcwcu">{`${officerName} time block ${index + 1} start`}</label>
                <input id={`${block.id}-start`} type="time" value={block.start} onChange={event => updateTime(block.id, 'start', event.target.value)} className={`w-[112px] rounded border bg-white px-1.5 py-1 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-rcm-500 ${valid ? 'border-slate-200' : 'border-red-300'}`} data-id="element-147" data-mp="3c54457xw0s1e" />
                <span className="text-xs text-slate-400" data-id="element-148" data-mp="3oskdcih822x">to</span>
                <label className="sr-only" htmlFor={`${block.id}-end`} data-id="element-149" data-mp="8n3vdqkicimq">{`${officerName} time block ${index + 1} end`}</label>
                <input id={`${block.id}-end`} type="time" value={block.end} onChange={event => updateTime(block.id, 'end', event.target.value)} className={`w-[112px] rounded border bg-white px-1.5 py-1 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-rcm-500 ${valid ? 'border-slate-200' : 'border-red-300'}`} data-id="element-150" data-mp="dmqbq8vmuezr" />
                <button type="button" onClick={() => removeBlock(block.id)} className="ml-auto flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rcm-500" aria-label={`Remove ${formatTimeBlock(block)} schedule for ${officerName}`} data-id="element-151" data-mp="1o5jmvdeelf2b">
                  <Trash2Icon className="h-3.5 w-3.5" data-id="element-152" data-mp="2755ye0j7ajac" />
                </button>
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1" role="group" aria-label={`Weekdays for ${officerName} time block ${index + 1}`} data-id="element-153" data-mp="34k1o3zerq94c">
                {WEEKDAYS.map(weekday => {
              const selected = block.days.includes(weekday.index);
              return <button key={weekday.index} type="button" onClick={() => updateDays(block.id, weekday.index)} aria-pressed={selected} title={weekday.name} className={`h-6 w-7 rounded text-[11px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rcm-500 ${selected ? 'bg-rcm-500 text-white hover:bg-rcm-600' : 'border border-slate-200 bg-white text-slate-400 hover:border-rcm-200 hover:text-rcm-600'}`} data-id="element-154" data-mp="2paes8dsnjsiv">
                      {weekday.short}
                    </button>;
            })}
              </div>
            </div>;
      })}

        {blocks.length === 0 && <p className="text-xs font-medium text-slate-400" data-id="element-155" data-mp="1fbvkp2j2es19">No scheduled availability</p>}
        {hasInvalidTimes && <p className="text-[11px] font-medium text-red-600" data-id="element-156" data-mp="1adkagqouyfii">End time must be after start time.</p>}
        {hasMissingDays && <p className="text-[11px] font-medium text-amber-700" data-id="element-157" data-mp="1p0ww0feo0l8w">Pick at least one weekday for each time block.</p>}
        <button type="button" onClick={addBlock} className="inline-flex items-center gap-1 text-xs font-bold text-rcm-600 hover:text-rcm-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rcm-500 focus-visible:ring-offset-1" data-id="element-158" data-mp="1k46h3veljvp2">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-159" data-mp="15e7kvy6lquc8" />
          Add time block
        </button>
      </div>
    </td>;
}